import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCustomerEventAdminTableDataRetrieveQueryKey,
  useCustomerEventsRollback,
} from "gen/endpoints/customer-event-admin/customer-event-admin";
import { CustomerEventRollbackAPIDataRequest } from "gen/model/customerEventRollbackAPIDataRequest";
import { CustomerEventRowMetadata } from "gen/model/customerEventRowMetadata";
import { FeatureFlagNameEnum } from "gen/model/featureFlagNameEnum";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import Form from "shared/components/form/Form";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { formatIsoDatetimeToDisplay } from "shared/utils/DateUtils";

export interface EventsRollbackConfirmDialogProps {
  fundFamilyId: string;
}

export interface EventsRollbackConfirmDialogArgs {
  events: CustomerEventRowMetadata[];
}

enum RollbackType {
  HARD = "hard",
  SOFT = "soft",
}

type RollbackFormData = Omit<
  CustomerEventRollbackAPIDataRequest,
  "soft_rollback"
> & {
  soft_rollback: RollbackType;
};

export const EventsRollbackConfirmDialog: React.FC<
  ConfirmationDialogProps<
    boolean,
    EventsRollbackConfirmDialogProps,
    EventsRollbackConfirmDialogArgs
  >
> = ({
  alertProps,
  onSuccess,
  onCancel,
  extraProps: { fundFamilyId },
  args: { events },
}) => {
  const { t } = useTranslation(["common", "events"]);
  const queryClient = useQueryClient();

  const refreshEventActive = useFeatureFlagActive(
    FeatureFlagNameEnum.refresh_out_of_date_events,
  );

  const { mutateAsync: rollbackEvents } = useCustomerEventsRollback();

  const form = useForm<RollbackFormData>();

  const currentRollbackType = form.watch("soft_rollback");

  const onSubmit = async (data: RollbackFormData) => {
    const customerEventIds = events.map((e) => e.id);
    await rollbackEvents({
      data: {
        fund_family_id: fundFamilyId,
        customer_event_ids: customerEventIds,
        soft_rollback: data.soft_rollback === RollbackType.SOFT,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: getCustomerEventAdminTableDataRetrieveQueryKey({
        fund_family_id: fundFamilyId,
      }),
    });
    onSuccess(true);
  };

  const lastEvent = events.at(-1)!;

  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit}>
          <ModalHeader>
            {t("events:rollback.finalize.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <VStack spacing="16px" alignItems="flex-start">
              <Box>
                <FormToggleButtonGroup
                  name="soft_rollback"
                  defaultValue={RollbackType.HARD}
                  hidden={!refreshEventActive}
                  containerStyles={{ width: "100%" }}
                >
                  <ToggleButton value={RollbackType.HARD}>
                    {t("events:rollback.finalize.hardRollback")}
                  </ToggleButton>
                  <ToggleButton value={RollbackType.SOFT}>
                    {t("events:rollback.finalize.softRollback")}
                  </ToggleButton>
                </FormToggleButtonGroup>
                <Alert status="info">
                  <AlertIcon>
                    <InfoCircleIcon />
                  </AlertIcon>
                  <AlertDescription>
                    {currentRollbackType === RollbackType.SOFT
                      ? t("events:rollback.finalize.softRollbackDescription")
                      : t("events:rollback.finalize.hardRollbackDescription")}
                  </AlertDescription>
                </Alert>
              </Box>
              <Box>
                <Text textStyle="bodyBold">
                  {t("events:rollback.finalize.rollbackFrom")}
                </Text>
                <Text>
                  {t("events:rollback.finalize.eventSummary", {
                    createdAt: formatIsoDatetimeToDisplay(lastEvent.created_at),
                    type: lastEvent.event_type_text,
                    subType: lastEvent.event_sub_type_text,
                  })}
                </Text>
              </Box>
              <Box>
                <Text textStyle="bodyBold">
                  {t("events:rollback.finalize.includedEvents")}
                </Text>
                <Text>{events.length}</Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={() => onCancel()} />}
              confirm={
                <FormSubmitButton>
                  {t("events:rollback.finalize.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
